import React, {useCallback, useMemo, useState} from 'react';
import {Box} from '@mui/material';
import {Typography} from '@mui/joy';
import {useTranslation} from 'react-i18next';
import {useQuery} from '@apollo/client';
import _ from 'lodash';
import {
    BasicModalContent,
    getInstalledBaseTypeIcon,
    LoadingScreen,
} from '@dreebit-registry/vsm-microservice-components';
import WizardNavigation from '../Wizard/WizardNavigation';
import InstalledBaseNodePathBreadcrumb from '../InstalledBaseNodePathBreadcrumb';
import NodeTypeSelectionItem from '../InstalledBaseTreeColumn/CreateNodeModal/nodeTypeSelectionItem';
import {InstalledBaseNodeListTdas} from '../../graphql/queries/installedBaseNodeListTdas';
import {InstalledBaseNodeTdas} from '../../graphql/queries/installedBaseNodeTdas';
import {separateInstalledBaseNodeCreateAttributes} from '../../utils/separateInstalledBaseNodeAttributes';
import {generateDuplicateTitles} from '../../utils/generateDuplicateTitles';
import AmountStep from './steps/AmountStep';
import TitlesStep from './steps/TitlesStep';
import CreateStep from './steps/CreateStep';
import {useSequentialNodeCreation} from './useSequentialNodeCreation';
import {DuplicateTemplate} from './types';

interface Props {
    template: DuplicateTemplate;
    onClose?: () => void;
    refetchFunction?: () => void;
}

const TOTAL_STEPS = 3;

const DuplicateNodeWizard = ({template, onClose, refetchFunction}: Props) => {
    const {t} = useTranslation();

    const [activeStep, setActiveStep] = useState(0);
    const [amount, setAmount] = useState(1);
    const [titles, setTitles] = useState<string[]>([]);

    // Attributes that are settable on create (for this parent + node type) ...
    const {data: createTdaData, loading: createTdasLoading, error: createTdasError} = useQuery(InstalledBaseNodeListTdas, {
        variables: {
            typeDefinitionIndex: 'INSTALLED_BASE_NODE_CREATE',
            options: {
                parentNodeId: template.parentNodeId,
                nodeTypeKey: template.nodeTypeKey,
            },
        },
        fetchPolicy: 'network-only',
    });

    // ... and the template node's current values to copy from.
    const {data: templateTdaData, loading: templateTdasLoading, error: templateTdasError} = useQuery(InstalledBaseNodeTdas, {
        variables: {
            id: template.templateNodeId,
        },
        fetchPolicy: 'network-only',
    });

    const tdasLoading = createTdasLoading || templateTdasLoading;
    const tdasError = createTdasError || templateTdasError;

    const createAttributes = _.get(createTdaData, 'installedBaseNodeList.typeDefinitionAttributeList.typeDefinitionAttributes', []);
    const templateAttributes = _.get(templateTdaData, 'installedBaseNode.typeDefinitionAttributeList.typeDefinitionAttributes', []);
    const templateNodeType = _.get(templateTdaData, 'installedBaseNode.nodeType');

    // Map the template's values onto the create attribute set once; only the
    // title varies between the duplicates.
    const baseVariables = useMemo(() => {
        if (!createAttributes.length) return null;

        const templateValuesByIndex: Record<string, any> = {};
        templateAttributes.forEach((attr: any) => {
            templateValuesByIndex[attr.index] = attr.value;
        });

        const formValues: Record<string, any> = {nodeTypeKey: template.nodeTypeKey};
        createAttributes.forEach((attr: any) => {
            if (attr.index === 'title' || attr.index === 'nodeTypeKey') return;
            const value = templateValuesByIndex[attr.index];
            if (value !== undefined && value !== null && value !== '') {
                formValues[attr.index] = value;
            }
        });

        return separateInstalledBaseNodeCreateAttributes(formValues, createAttributes);
    }, [createAttributes, templateAttributes, template.nodeTypeKey]);

    const buildVariables = useCallback((title: string) => {
        const {input, dynamicLists} = baseVariables!;
        return {
            parentNodeId: template.parentNodeId,
            input: {...input, title},
            ...Object.fromEntries(
                Object.entries(dynamicLists).map(([key, list]) => [key, list.length > 0 ? list : undefined])
            ),
        };
    }, [baseVariables, template.parentNodeId]);

    const {
        items,
        start,
        retry,
        cancel,
        started,
        running,
        cancelled,
        successCount,
        errorCount,
        percent,
        etaMs,
    } = useSequentialNodeCreation({buildVariables, refetchFunction});

    const hasBlankTitle = titles.some((title) => !title.trim());

    const handleNext = () => {
        if (activeStep === 0) {
            // Prefill new indexes with suggestions, keep what the user already typed.
            setTitles((prev) => {
                const suggestions = generateDuplicateTitles(template.templateTitle, amount);
                return Array.from({length: amount}, (_, i) => prev[i] ?? suggestions[i]);
            });
            setActiveStep(1);
            return;
        }
        if (activeStep === 1) {
            if (hasBlankTitle || !baseVariables) return;
            setActiveStep(2);
            start(titles.map((title) => title.trim()));
        }
    };

    const handleBack = () => {
        if (activeStep > 0 && !started) {
            setActiveStep((prev) => prev - 1);
        }
    };

    const getStepHeading = () => {
        switch (activeStep) {
            case 0:
                return t('Number of duplicates');
            case 1:
                return t('Titles');
            default:
                return running
                    ? t('{{amount}} nodes are being created', {amount: items.length})
                    : t('Creation completed');
        }
    };

    const getStepExplanation = () => {
        switch (activeStep) {
            case 0:
                return t('Select the number of copies. All information will be copied from the original node {{title}}.', {title: template.templateTitle});
            case 1:
                return t('Review and adjust the titles of the new nodes. You can edit each title individually.');
            default:
                if (running) return t('The nodes are being created. Please keep this window open.');
                if (errorCount > 0) return t('Some nodes could not be created.');
                return t('All nodes have been created successfully.');
        }
    };

    const renderStepContent = () => {
        if (activeStep < 2 && tdasError) {
            return (
                <Typography level="body-md" sx={{color: 'error.main', py: 2}}>
                    {t('Error loading node data.')}
                </Typography>
            );
        }
        switch (activeStep) {
            case 0:
                return <AmountStep amount={amount} onAmountChange={setAmount}/>;
            case 1:
                return tdasLoading
                    ? <LoadingScreen size="md"/>
                    : <TitlesStep titles={titles} onTitleChange={(index, value) => {
                        setTitles((prev) => prev.map((title, i) => (i === index ? value : title)));
                    }}/>;
            default:
                return (
                    <CreateStep
                        items={items}
                        running={running}
                        cancelled={cancelled}
                        percent={percent}
                        successCount={successCount}
                        errorCount={errorCount}
                        etaMs={etaMs}
                        onRetry={retry}
                        onCancel={cancel}
                        onClose={onClose}
                    />
                );
        }
    };

    return (
        <BasicModalContent
            title={
                <Box flexDirection={'row'} display={'flex'} alignItems={'center'}>
                    <Typography level={'titleSmallLight'}>{t('Duplicate')}</Typography>
                    <Box display={'flex'} alignItems={'center'} sx={{ml: '8px', gap: '3px'}}>
                        {getInstalledBaseTypeIcon(template.nodeTypeKey, 18)}
                        <Typography level={'titleSmall'}>{template.templateTitle}</Typography>
                    </Box>
                </Box>
            }
            onClose={onClose}
        >
            <Box sx={{mt: 0.5, mb: 1}}>
                <InstalledBaseNodePathBreadcrumb nodeId={template.parentNodeId}/>
            </Box>

            {/* Read-only card showing the template's node type (the step progress
                is already covered by the navigation bar at the bottom). */}
            {templateNodeType && (
                <Box sx={{mt: 1}}>
                    <NodeTypeSelectionItem nodeType={templateNodeType}/>
                </Box>
            )}

            <Box sx={{py: 2}}>
                <Typography level="headline">{getStepHeading()}</Typography>
                <Typography level="body-sm" sx={{color: 'text.secondary', mb: 1}}>
                    {getStepExplanation()}
                </Typography>
                {renderStepContent()}
            </Box>

            {activeStep < 2 && (
                <WizardNavigation
                    activeStep={activeStep}
                    totalSteps={TOTAL_STEPS}
                    onBack={handleBack}
                    onNext={handleNext}
                    onFinish={() => undefined}
                    isNextDisabled={!!tdasError || (activeStep === 1 && (hasBlankTitle || tdasLoading || !baseVariables))}
                    nextButtonText={activeStep === 1 ? t('Create') : undefined}
                />
            )}
        </BasicModalContent>
    );
};

export default DuplicateNodeWizard;
